import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Globe, Target, Users, Ship, Linkedin } from "lucide-react";
import { ChevronRight } from "../components/DirectionalIcon";
import { COMPANY_STATS } from "../constants/stats";
import { buildLocalizedPath } from "../constants/languages";

const SHIPPING_BG = "https://images.unsplash.com/photo-1760385280608-935bfeaab4ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXJzJTIwb2NlYW4lMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzczNTMxMTIzfDA&ixlib=rb-4.1.0&w=1600&q=85";

const AboutPage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t("about.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("about.meta.description"));
    }
  }, [t]);

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-navy text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${SHIPPING_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">
              {t("about.hero.kicker")}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mt-4 mb-6">
              {t("about.hero.title")}<br />
              <span className="text-gold">{t("about.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl leading-relaxed">
              {t("about.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("about.story.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4 mb-6">
                {t("about.story.title")}
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  {t("about.story.paragraph1")}
                </p>
                <p>
                  {t("about.story.paragraph2")}
                </p>
                <p>
                  {t("about.story.paragraph3")}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-navy text-white p-8">
                <Building2 className="text-gold w-10 h-10 mb-4" strokeWidth={1.5} />
                <div className="font-outfit text-4xl font-bold">2000</div>
                <p className="text-slate-400 text-sm mt-2">{t("about.story.stat1Label")}</p>
              </div>
              <div className="bg-slate-100 p-8">
                <Ship className="text-gold w-10 h-10 mb-4" strokeWidth={1.5} />
                <div className="font-outfit text-4xl font-bold text-navy">1000+</div>
                <p className="text-slate-500 text-sm mt-2">{t("about.story.stat2Label")}</p>
              </div>
              <div className="bg-slate-100 p-8">
                <Globe className="text-gold w-10 h-10 mb-4" strokeWidth={1.5} />
                <div className="font-outfit text-4xl font-bold text-navy">70+</div>
                <p className="text-slate-500 text-sm mt-2">{t("about.story.stat3Label")}</p>
              </div>
              <div className="bg-gold text-navy p-8">
                <Users className="w-10 h-10 mb-4" strokeWidth={1.5} />
                <div className="font-outfit text-4xl font-bold">2186+</div>
                <p className="text-navy/70 text-sm mt-2">{t("about.story.stat4Label")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Target className="text-gold w-12 h-12 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mb-6">
              {t("about.mission.title")}
            </h2>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              {t("about.mission.description")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("about.values.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("about.values.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: t("about.values.card1.title"),
                description: t("about.values.card1.description", { years: COMPANY_STATS.yearsExperience }),
              },
              {
                title: t("about.values.card2.title"),
                description: t("about.values.card2.description"),
              },
              {
                title: t("about.values.card3.title"),
                description: t("about.values.card3.description"),
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-hover bg-white border border-slate-200 p-8"
              >
                <div className="w-12 h-12 bg-navy flex items-center justify-center mb-6">
                  <span className="font-outfit text-gold text-2xl font-bold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-outfit text-xl font-semibold text-navy mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LinkedIn Section */}
      <section className="py-20 md:py-32 bg-slate-100">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mb-4">
              {t("about.linkedin.title")} <span className="text-gold">{t("about.linkedin.titleAccent")}</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("about.linkedin.description")}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-navy rounded-xl p-8 md:p-12 border-2 border-gold text-center">
              <div className="w-20 h-20 bg-[#0A66C2] rounded-xl flex items-center justify-center mx-auto mb-6">
                <Linkedin size={40} className="text-white" />
              </div>
              
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-2">
                {t("about.linkedin.companyName")}
              </h3>
              <p className="text-gold mb-6">{t("about.linkedin.tagline")}</p>

              <p className="text-slate-400 mb-8">
                {t("about.linkedin.subheading")}
              </p>

              <div className="flex justify-center gap-8 mb-8 text-center">
                <div>
                  <p className="text-gold font-outfit text-xl font-bold">{t("about.linkedin.stat1Value")}</p>
                  <p className="text-slate-500 text-sm">{t("about.linkedin.stat1Label")}</p>
                </div>
                <div>
                  <p className="text-gold font-outfit text-xl font-bold">{COMPANY_STATS.countries}</p>
                  <p className="text-slate-500 text-sm">{t("about.linkedin.stat2Label")}</p>
                </div>
                <div>
                  <p className="text-gold font-outfit text-xl font-bold">{COMPANY_STATS.containers}</p>
                  <p className="text-slate-500 text-sm">{t("about.linkedin.stat3Label")}</p>
                </div>
              </div>

              <a
                href="https://www.linkedin.com/in/mushair-nawaz-726a5b8a"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin-cta"
                className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gold-light transition-colors rounded-lg"
              >
                {t("about.linkedin.cta")}
                <ChevronRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-navy text-white relative noise-overlay">
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
            {t("about.cta.title")}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10">
            {t("about.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={buildLocalizedPath("/contact", i18n.language)}
              data-testid="about-contact-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
            >
              {t("about.cta.contactButton")}
              <ChevronRight size={16} />
            </Link>
            <Link
              to={buildLocalizedPath("/sell-panels", i18n.language)}
              data-testid="about-sell-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white/5 transition-colors"
            >
              {t("about.cta.sellButton")}
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
